import { MatchClutchDummyData } from "../utils/dummyData";
import api from "./axios.config";
import {
  OtpCodeSendPayload,
  OtpCodeVerifyPayload,
  OtpCodeVerifySuccessPayload,
  UserModel,
  SpecificBookingSlotsResponse,
  Player,
  CirclePlayersList,
  ProductsServices,
  CourtBookingPayload,
  InvitePlayersPayload,
  AddPaymentRequestPayload,
  PaymentCard,
  BookedCourtsResponse,
  PlayerPrices,
  ExternalWalletBalance,
  CancelBookingResponse,
  MatchResponsePayload,
  MatchClutchStatsResponse,
} from "./types";

export class CourtBookingApi {
  static async otpCodeSend(request: OtpCodeSendPayload) {
    try {
      const response = await api.post(
        "/customer/Authentication/otp/start",
        request
      );
      return response.data;
    } catch (error) {
      console.error("otpCodeSend - Error: ", error);
      throw error;
    }
  }

  static async otpCodeVerify(request: OtpCodeVerifyPayload) {
    try {
      const response = await api.post<OtpCodeVerifySuccessPayload>(
        "/customer/Authentication/otp/complete",
        request
      );
      return response.data;
    } catch (error) {
      console.error("otpCodeVerify - Error: ", error);
      throw error;
    }
  }

  static async getMe() {
    try {
      const response = await api.get<UserModel>("/customer/Me");
      return response.data;
    } catch (error) {
      console.error("otpCodeVerify - Error: ", error);
      throw error;
    }
  }

  static async getTimeSlots(
    selectedDate: string,
    isHourlySlot: boolean,
    locationId: number
  ) {
    try {
      const response = await api.get<SpecificBookingSlotsResponse>(
        `/customer/Booking/specificBookingSlots?date=${selectedDate}&locationId=${locationId}&isHourlySlot=${isHourlySlot}`
      );
      return response.data;
    } catch (error) {
      console.error("getTimeSlots - Error: ", error);
      throw error;
    }
  }

  static async getPlayersList(pageNum: number, filtername?: string) {
    const url = `/customer/Match/users?filtername=${
      filtername ?? ""
    }&pageNum=${pageNum}`;
    try {
      const response = await api.get<Player[]>(url);
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async getCirclePlayersList(lowerLimit: number, upperLimit: number) {
    const url = `/customer/Booking/circlePlayers?lowerLimit=${lowerLimit}&upperLimit=${upperLimit}`;
    try {
      const response = await api.get<CirclePlayersList>(url);
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async getProductAndServices(
    startSlotTime: number,
    endSlotTime: number,
    courtType: number
  ) {
    const url = `/customer/Booking/products?startSlotTime=${startSlotTime}&endSlotTime=${endSlotTime}&courtType=${courtType}`;
    try {
      const response = await api.get<ProductsServices[]>(url);
      const updatedResponse = response.data.map((category) => ({
        ...category,
        products: category.products.map((product) => ({
          ...product,
          count: 0,
          selectedPlayers: [],
          totalSalePrice: product.netPrice + product.taxPrice,
          categoryId: category.categoryId,
        })),
      }));
      return updatedResponse;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async bookMatch(payload: CourtBookingPayload) {
    const url = "/customer/Booking/court";
    try {
      const response = await api.post(url, payload);
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async bookMatchV2(payload: CourtBookingPayload) {
    const url = "/customer/Booking/court-v2";
    try {
      const response = await api.post(url, payload);
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async sendInvitationToPlayers(payload: InvitePlayersPayload) {
    try {
      const response = await api.post(
        "/customer/Booking/invitePlayers",
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async postPaymentMethod(request: AddPaymentRequestPayload) {
    try {
      const response = await api.post("/customer/Me/paymentmethods", request);
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async getPaymentMethod() {
    try {
      const response = await api.get<PaymentCard[]>(
        "/customer/Me/paymentmethods"
      );
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async setDefaultPaymentMethod(id: number) {
    try {
      const response = await api.put(
        `/customer/Me/paymentmethods/${id}/default`
      );
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async getBookingList() {
    try {
      const response = await api.get<BookedCourtsResponse[]>(
        `/customer/Booking/time/1`
      );
      const bookedCourts = response.data.map((bookedCourt) => ({
        ...bookedCourt,
        bookingDate: bookedCourt.bookingDate.includes("Z")
          ? bookedCourt.bookingDate
          : `${bookedCourt.bookingDate}Z`,
        createdDate: bookedCourt.createdDate.includes("Z")
          ? bookedCourt.createdDate
          : `${bookedCourt.createdDate}Z`,
      }));

      return bookedCourts;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async deleteBooking(id: number) {
    try {
      const response = await api.delete(`/customer/Booking/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async getDemoMatchDetails(guid: string) {
    try {
      const response = await api.get<MatchResponsePayload>(
        `/customer/UnAuthenticated/match/${guid}`
      );
      const updatedResponse: MatchResponsePayload = {
        ...response.data,
        isEditable:
          new Date() <=
          new Date(
            response.data?.scores?.[0]?.editableUntil.includes("Z")
              ? response.data?.scores?.[0]?.editableUntil
              : `${response.data?.scores?.[0]?.editableUntil}Z`
          ),
        isAnyScoreAdded: response.data?.scores?.some(
          (p) => p.score !== null && p?.score?.toString() !== ""
        ),
        date: response.data.date.includes("Z")
          ? response.data.date
          : `${response.data.date}Z`,
        scores: response.data.scores.map((score) => ({
          ...score,
          editableUntil: score.editableUntil.includes("Z")
            ? score.editableUntil
            : `${score.editableUntil}Z`,
        })),
      };
      return updatedResponse;
    } catch (error) {
      console.log("Error: ", error);
      throw error;
    }
  }

  static async getDemoMatchClutchStatus(guid: string) {
    try {
      const response = await api.get<MatchClutchStatsResponse>(
        `/customer/UnAuthenticated/match-stats/${guid}`
      );
      const mutatedResponse = {
        ...response.data,
        ...MatchClutchDummyData,
        apiPlayerDetectedShots:
          MatchClutchDummyData.apiPlayerDetectedShots?.map((shots) => {
            return {
              ...shots,
              individualStats: shots.individualStats?.map((stat) => {
                return {
                  ...stat,
                  playerDetectedShotsList: stat.playerDetectedShotsList?.filter(
                    (v) => v.shotDirection !== "unknown"
                  ),
                };
              }),
            };
          }),
      };

      return mutatedResponse;
    } catch (error) {
      console.log("Error:", error);
      throw error;
    }
  }

  static async getPlayerPricesList(
    startSlotTime: number,
    endSlotTime: number,
    courtType: number,
    player1: number,
    player2: number,
    player3: number
  ) {
    try {
      // Build the base URL
      let url = `/customer/Booking/prices?startSlotTime=${startSlotTime}&endSlotTime=${endSlotTime}&courtType=${courtType}`;

      // Only add playerIds that have values
      if (player1) url += `&playerIds=${player1}`;
      if (player2) url += `&playerIds=${player2}`;
      if (player3) url += `&playerIds=${player3}`;

      const response = await api.get<PlayerPrices>(url);
      return {
        ...response.data,
        playerPrices: response.data.playerPrices?.map((price) => ({
          ...price,
          product: {
            ...price.product,
            totalPrice:
              (price.product?.netPrice ?? 0) + (price.product?.taxPrice ?? 0),
          },
        })),
      };
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async getExternalWalletBalance() {
    try {
      const response = await api.get<ExternalWalletBalance>(
        "/customer/Me/ext-wallet"
      );
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }

  static async dropFromBooking(bookingId: number) {
    try {
      const response = await api.delete<CancelBookingResponse>(
        `/customer/Booking/${bookingId}/playerDrop`
      );
      return response.data;
    } catch (error) {
      console.error("Error: ", error);
      throw error;
    }
  }
  static async getMatchDetails(matchId: number) {
    try {
      const response = await api.get<MatchResponsePayload>(
        `/customer/Match/${matchId}`
      );
      const updatedResponse: MatchResponsePayload = {
        ...response.data,
        isEditable:
          new Date() <=
          new Date(
            response.data?.scores?.[0]?.editableUntil.includes("Z")
              ? response.data?.scores?.[0]?.editableUntil
              : `${response.data?.scores?.[0]?.editableUntil}Z`
          ),
        isAnyScoreAdded: response.data?.scores?.some(
          (p) => p.score !== null && p?.score?.toString() !== ""
        ),
        date: response.data.date.includes("Z")
          ? response.data.date
          : `${response.data.date}Z`,
        scores: response.data.scores.map((score) => ({
          ...score,
          editableUntil: score.editableUntil.includes("Z")
            ? score.editableUntil
            : `${score.editableUntil}Z`,
        })),
      };
      return updatedResponse;
    } catch (error) {
      console.log("Error: ", error);
      throw error;
    }
  }
}

export const getMatchClutchStats = async (matchId: number) => {
  try {
    const response = await api.get<MatchClutchStatsResponse>(
      `customer/Match/${matchId}/clutch-stats`
    );
    const mutatedResponse = {
      ...response.data,
      apiPlayerDetectedShots: response.data.apiPlayerDetectedShots?.map(
        (shots) => {
          return {
            ...shots,
            individualStats: shots.individualStats?.map((stat) => {
              return {
                ...stat,
                playerDetectedShotsList: stat.playerDetectedShotsList?.filter(
                  (v) => v.shotDirection !== "unknown"
                ),
              };
            }),
          };
        }
      ),
    };

    return mutatedResponse;
  } catch (error) {
    console.log("Error:", error);
    throw error;
  }
};
